/**
 * Inline player for the recorded assistant calls: play/pause, a waveform drawn
 * from the recording's own peaks, and a scrubber.
 *
 * Nothing is fetched and nothing makes a sound until the visitor presses play —
 * the audio element carries preload="none" and no autoplay anywhere.
 *
 * Progressive enhancement: the markup ships a <noscript> native player, so a
 * failed bundle still leaves a working, in-page way to listen.
 */

const SEEK_STEPS = 1000

/** Bar plus gap, in px — the waveform thins out to keep this rhythm at any width. */
const BAR_PITCH = 5
const MIN_BARS = 16

function requireIn<T extends HTMLElement>(root: HTMLElement, selector: string): T {
  const el = root.querySelector<T>(selector)
  if (!el) throw new Error(`Expected "${selector}" inside the voice player markup.`)
  return el
}

/** Seconds as m:ss — the length of a phone call, never an hour. */
function formatTime(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds))
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`
}

/**
 * Averages the recording's peaks down to `count` bars. A fixed bar count would
 * either crowd into hairlines on a narrow card or stretch into planks on a wide
 * one, so the waveform is redrawn to suit whatever width it ends up with.
 */
function resample(peaks: number[], count: number): number[] {
  if (count >= peaks.length) return peaks

  const out: number[] = []
  for (let bar = 0; bar < count; bar++) {
    const from = Math.floor((bar * peaks.length) / count)
    const to = Math.max(from + 1, Math.floor(((bar + 1) * peaks.length) / count))
    let sum = 0
    for (let i = from; i < to; i++) sum += peaks[i] ?? 0
    out.push(Math.round(sum / (to - from)))
  }
  return out
}

/** One row of waveform bars, heights driven by the recording's peaks. */
function buildLayer(peaks: number[], className: string): HTMLElement {
  const layer = document.createElement('div')
  layer.className = className
  for (const peak of peaks) {
    const bar = document.createElement('i')
    bar.style.setProperty('--h', String(peak))
    layer.append(bar)
  }
  return layer
}

function initPlayer(root: HTMLElement): void {
  const audio = requireIn<HTMLAudioElement>(root, '.vp-audio')
  const button = requireIn<HTMLButtonElement>(root, '.vp-play')
  const seek = requireIn<HTMLInputElement>(root, '.vp-seek')
  const bars = requireIn(root, '.vp-bars')
  const now = requireIn(root, '.vp-now')
  const total = requireIn(root, '.vp-total')
  const status = requireIn(root, '.vp-status')

  // The clip length is in the markup so the scrubber and the "2:26" read true
  // before a single byte has been downloaded. The file itself wins once known.
  const declared = Number(root.dataset['duration'] ?? '0')
  const duration = (): number => (Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : declared)

  const peaks = (root.dataset['peaks'] ?? '')
    .split(',')
    .map(Number)
    .filter((peak) => Number.isFinite(peak))

  let barCount = 0
  function drawWaveform(width: number): void {
    const wanted = Math.max(MIN_BARS, Math.min(peaks.length, Math.floor(width / BAR_PITCH)))
    if (wanted === barCount) return
    barCount = wanted
    const sampled = resample(peaks, wanted)
    bars.replaceChildren(buildLayer(sampled, 'vp-layer'), buildLayer(sampled, 'vp-layer vp-fill'))
  }

  drawWaveform(bars.clientWidth)
  if ('ResizeObserver' in window) {
    new ResizeObserver(([entry]) => {
      if (entry) drawWaveform(entry.contentRect.width)
    }).observe(bars)
  }

  total.textContent = formatTime(declared)

  let scrubbing = false

  function paint(seconds: number): void {
    const fraction = duration() > 0 ? Math.min(1, seconds / duration()) : 0
    root.style.setProperty('--p', `${fraction * 100}%`)
    now.textContent = formatTime(seconds)
    if (!scrubbing) seek.value = String(Math.round(fraction * SEEK_STEPS))
  }

  function setLabel(): void {
    button.setAttribute('aria-label', audio.paused ? 'Play the recording' : 'Pause the recording')
  }

  button.addEventListener('click', () => {
    if (audio.paused) {
      root.classList.add('is-loading')
      status.textContent = 'connecting…'
      // Chrome and Safari reject play() on a stalled network or a hostile
      // autoplay policy; surface the native controls rather than a dead button.
      audio.play().catch(() => {
        root.classList.remove('is-loading')
        root.classList.add('is-broken')
        status.textContent = "couldn't play"
        audio.hidden = false
        audio.controls = true
      })
    } else {
      audio.pause()
    }
  })

  audio.addEventListener('playing', () => {
    root.classList.remove('is-loading')
    root.classList.add('is-playing')
    status.textContent = '● playing'
    setLabel()
  })

  audio.addEventListener('pause', () => {
    root.classList.remove('is-playing', 'is-loading')
    status.textContent = 'paused'
    setLabel()
  })

  audio.addEventListener('waiting', () => root.classList.add('is-loading'))

  audio.addEventListener('ended', () => {
    audio.currentTime = 0
    paint(0)
    status.textContent = 'that was the whole call'
  })

  audio.addEventListener('timeupdate', () => {
    if (!scrubbing) paint(audio.currentTime)
  })

  audio.addEventListener('loadedmetadata', () => {
    total.textContent = formatTime(duration())
    paint(audio.currentTime)
  })

  // The range input sits invisibly over the waveform: pointer and keyboard
  // seeking come free, and screen readers get a control they already know.
  seek.addEventListener('pointerdown', () => { scrubbing = true })
  seek.addEventListener('input', () => {
    paint((Number(seek.value) / SEEK_STEPS) * duration())
  })
  seek.addEventListener('change', () => {
    scrubbing = false
    audio.currentTime = (Number(seek.value) / SEEK_STEPS) * duration()
  })

  setLabel()
  paint(0)
}

/** Wires every `.vp` on the page. Silent no-op on pages that have none. */
export function initVoicePlayers(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('.vp').forEach(initPlayer)
}
